import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, Megaphone, CheckCheck, Send, AlertTriangle, ArrowRight, Plus, Loader2, RefreshCcw } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import StatusBadge from '@/components/StatusBadge';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value, hint, accent, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-4 sm:p-5"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs sm:text-sm text-muted-foreground">{label}</div>
          <div className="text-2xl sm:text-3xl font-bold tabular-nums mt-1">{value}</div>
          {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
        </div>
        <div className={`h-10 w-10 rounded-xl grid place-items-center ${accent || 'bg-[#FFE2D1] text-[#9A3412]'}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </motion.div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/stats');
      setStats(r.data);
    } catch (e) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const syncTemplates = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/templates/sync-from-meta');
      toast.success(`Synced ${r.data.synced} template(s) from Meta`);
      await load();
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5 sm:space-y-7">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="dashboard-heading">
            Hello, <span className="text-gradient-orange">Admin</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Here's a quick view of your WhatsApp marketing performance.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="bg-white/60 border-white/60 rounded-xl" onClick={syncTemplates} disabled={syncing} data-testid="dashboard-sync-templates-button">
            {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1.5" />}
            Sync Templates
          </Button>
          <Link to="/campaigns"><Button className="gradient-orange text-white rounded-xl" data-testid="dashboard-create-campaign-button"><Plus className="h-4 w-4 mr-1.5" />New Campaign</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <StatCard testid="dashboard-kpi-total-contacts" icon={Users} label="Total Contacts" value={loading ? '—' : (stats?.total_contacts ?? 0)} hint={`${stats?.total_groups ?? 0} groups`} accent="bg-[#FFE2D1] text-[#9A3412]" />
        <StatCard testid="dashboard-kpi-approved-templates" icon={FileText} label="Approved Templates" value={loading ? '—' : (stats?.approved_templates ?? 0)} hint={`${stats?.pending_templates ?? 0} pending`} accent="bg-[#EAF8F1] text-[#0F6B45]" />
        <StatCard testid="dashboard-kpi-completed-campaigns" icon={Megaphone} label="Completed Campaigns" value={loading ? '—' : (stats?.completed_campaigns ?? 0)} hint={`${stats?.scheduled_campaigns ?? 0} scheduled`} accent="bg-[#E6F6FF] text-[#075985]" />
        <StatCard testid="dashboard-kpi-messages-sent" icon={Send} label="Messages Sent" value={loading ? '—' : (stats?.total_messages_sent ?? 0)} hint={`${stats?.total_messages_failed ?? 0} failed`} accent="bg-[#FFF3E8] text-[#9A3412]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        <div className="glass-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recent Campaigns</h2>
            <Link to="/campaigns" className="text-sm text-[hsl(var(--primary))] hover:underline" data-testid="dashboard-view-all-campaigns">View all <ArrowRight className="inline h-3.5 w-3.5" /></Link>
          </div>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Loading...</div>
          ) : !stats?.recent_campaigns?.length ? (
            <div className="py-10 text-center">
              <div className="inline-flex h-12 w-12 rounded-2xl bg-[#FFE2D1] text-[#9A3412] items-center justify-center mb-3">
                <Megaphone className="h-6 w-6" />
              </div>
              <div className="text-sm text-muted-foreground mb-3">No campaigns yet. Create your first WhatsApp marketing campaign.</div>
              <Link to="/campaigns"><Button className="gradient-orange text-white rounded-xl" data-testid="dashboard-empty-create-campaign">Create Campaign</Button></Link>
            </div>
          ) : (
            <ul className="divide-y divide-white/50">
              {stats.recent_campaigns.map((c) => (
                <li key={c.id} className="py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <Link to={`/campaigns/${c.id}`} className="font-medium hover:underline truncate block" data-testid={`recent-campaign-${c.id}`}>
                      {c.name}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {(c.sent_count ?? 0)}/{(c.total_count ?? 0)} sent · {(c.failed_count ?? 0)} failed
                    </div>
                  </div>
                  <StatusBadge status={c.status} kind="campaign" />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="glass-card p-5">
          <h2 className="text-lg font-semibold mb-3">Delivery Snapshot</h2>
          <div className="space-y-3">
            <DeliveryRow icon={Send} label="Sent" value={stats?.total_messages_sent ?? 0} color="text-[#9A3412]" bg="bg-[#FFF7ED]" />
            <DeliveryRow icon={CheckCheck} label="Delivered" value={stats?.total_messages_delivered ?? 0} color="text-[#0F6B45]" bg="bg-[#EAF8F1]" />
            <DeliveryRow icon={CheckCheck} label="Read" value={stats?.total_messages_read ?? 0} color="text-[#075985]" bg="bg-[#E6F6FF]" />
            <DeliveryRow icon={AlertTriangle} label="Failed" value={stats?.total_messages_failed ?? 0} color="text-[#9F1239]" bg="bg-[#FEECEE]" />
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            Note: Live delivery/read updates require Meta webhooks. V1 shows sent/failed in real-time.
          </div>
        </div>
      </div>
    </div>
  );
}

function DeliveryRow({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`h-8 w-8 rounded-xl ${bg} ${color} grid place-items-center`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 text-sm">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
    </div>
  );
}
